import React, { CSSProperties } from 'react';
import { ResponsiveHidingProps, ResponsivePaddingProps, useFullTheme } from '~/design';
import { Responsive } from '~/design/breakpoint';
import { SpacingToken } from '~/design/spacing';
import { useResponsive } from '~/utils';
import { useResponsiveHideStyle, useResponsivePaddingStyle } from '~/utils/styling';

type Gap = Exclude<SpacingToken, '1px' | '2px'>;

export interface GridAlphaProps extends ResponsivePaddingProps, ResponsiveHidingProps {
  /** Equivalent to grid-template-areas, specifies named grid areas, establishing the cells in the grid and assigning them names. */
  areas?: string[] | Responsive<string[]>;

  /** The grid contents. */
  children: React.ReactNode;

  /** The gap between columns. */
  columnGap?: Gap | Responsive<Gap>;

  /** Equivalent to grid-template-columns, defines the line names and track sizing functions of the grid columns. */
  columns?: string | Responsive<string>;

  /** The gap between rows. */
  rowGap?: Gap | Responsive<Gap>;

  /** Equivalent to grid-template-rows, defines the line names and track sizing functions of the grid rows. */
  rows?: string | Responsive<string>;
}

/**
 * Based on CSS Grid, a grid creates visual consistency between layouts while allowing flexibility across a wide variety of designs.
 * For details about CSS Grid, refer to this article: https://css-tricks.com/snippets/css/complete-guide-grid/ . You can also use a
 * tool to help you visualize how to implement your Grid: https://grid.layoutit.com/
 */
export function GridAlpha({
  areas,
  children,
  columnGap,
  columns,
  hideAbove,
  hideBelow,
  p,
  pb,
  pl,
  pr,
  pt,
  px,
  py,
  rowGap,
  rows,
}: GridAlphaProps) {
  const { spacing } = useFullTheme();
  const responsive = useResponsive();
  const paddingStyle = useResponsivePaddingStyle({
    p,
    pb,
    pl,
    pr,
    pt,
    px,
    py,
  });

  const hideStyle = useResponsiveHideStyle({ hideBelow, hideAbove });

  return (
    <div
      css={[
        {
          display: 'grid',
        },
        paddingStyle,
        hideStyle,
        responsive.css({
          columnGap: columnGap && responsive.map(columnGap, prop => spacing[prop]),
          gridTemplateAreas:
            areas && responsive.map(areas, prop => prop && `"${prop.join('" "')}"`),
          gridTemplateColumns: columns,
          gridTemplateRows: rows,
          rowGap: rowGap && responsive.map(rowGap, prop => spacing[prop]),
        }),
      ]}
    >
      {children}
    </div>
  );
}

export interface GridAlphaItemProps {
  /** Sets how will Item be aligned in the grid area */
  align?: CSSProperties['alignSelf'] | Responsive<CSSProperties['alignSelf']>;

  /** Specifies where the Item will be placed in relation to the areas created on the parent element. */
  areaName?: string;

  /** The content of the grid item. */
  children: React.ReactNode;

  /** Specifies the item's end position within the grid columns */
  columnEnd?: CSSProperties['gridColumnEnd'] | Responsive<CSSProperties['gridColumnEnd']>;

  /** Specifies the item's start position within the grid columns */
  columnStart?: CSSProperties['gridColumnStart'] | Responsive<CSSProperties['gridColumnStart']>;

  /** Specifies the item's end position within the grid rows */
  rowEnd?: CSSProperties['gridRowEnd'] | Responsive<CSSProperties['gridRowEnd']>;

  /** Specifies the item's start position within the grid rows */
  rowStart?: CSSProperties['gridRowStart'] | Responsive<CSSProperties['gridRowStart']>;
}

/**
 * GridAlpha.Item is an optional component when using GridAlpha.
 */
GridAlpha.Item = function GridAlphaItem({
  align,
  areaName,
  children,
  columnEnd,
  columnStart,
  rowEnd,
  rowStart,
}: GridAlphaItemProps) {
  const responsive = useResponsive();
  return (
    <div
      css={[
        {
          gridArea: areaName,
          minWidth: 0,
        },
        responsive.css({
          alignSelf: align,
          gridColumnStart: columnStart,
          gridColumnEnd: columnEnd,
          gridRowStart: rowStart,
          gridRowEnd: rowEnd,
        }),
      ]}
    >
      {children}
    </div>
  );
};
